import { useEffect, useMemo, useRef, useState } from "react";
import type { KocListItem } from "@koc/shared";
import { Input } from "./ui.js";

// Simple type-to-filter KOC picker. Pick from the existing list; no auto-create. The
// VideoImportDialog has its own variant that exposes the typed name to its parent for
// auto-create-on-submit; project pages don't need that since they reference existing KOCs.
export function KocPicker({
  kocs,
  value,
  onChange,
  placeholder = "search KOC…",
}: {
  kocs: KocListItem[];
  value: number | null | undefined;
  onChange: (kocId: number | null) => void;
  placeholder?: string;
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = value != null ? kocs.find((k) => k.id === value) ?? null : null;

  // When selection lands (via pick), clear local search so the resolved name is shown.
  useEffect(() => {
    if (selected) setSearch("");
  }, [selected]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const matches = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return kocs.slice(0, 20);
    return kocs.filter((k) => k.name.toLowerCase().includes(s)).slice(0, 20);
  }, [search, kocs]);

  const displayValue = selected ? selected.name : search;

  return (
    <div className="relative flex-1" ref={wrapRef}>
      <Input
        value={displayValue}
        placeholder={placeholder}
        onChange={(e) => {
          // Typing while a koc is selected releases the selection so the user can search again.
          if (selected) onChange(null);
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {selected && (
        <button
          type="button"
          aria-label="clear KOC"
          onClick={() => { onChange(null); setSearch(""); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 text-sm leading-none"
        >
          ×
        </button>
      )}
      {open && (
        <ul className="absolute z-20 mt-1 w-full max-h-64 overflow-auto rounded border border-stone-300 bg-white shadow-lg text-sm">
          {matches.length === 0 ? (
            <li className="px-2 py-2 text-xs text-stone-500">
              No KOC matches “{search}”. Add one on the KOCs page first.
            </li>
          ) : (
            matches.map((k) => (
              <li
                key={k.id}
                className="flex items-center justify-between px-2 py-1.5 hover:bg-stone-100 cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(k.id);
                  setSearch("");
                  setOpen(false);
                }}
              >
                <span className="truncate">{k.name}</span>
                <span className="text-xs text-stone-500 ml-2 whitespace-nowrap">
                  {k.video_count} videos
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
